
import React from "react";
import { i18nManager } from "../../../i18n";
import { Schemes, Utils } from "../../../utils";
import { Elements } from "../../shared/Elements";
import * as ReactRouterDom from "react-router-dom";
import { Classname, DateHelper, DateTimeInput, DateTimePickerLayout, DateTimePickerSelectionMode, FormControl, ChartType, ChartData, Table, Column, useScreenDimension, StringHelper } from "@rivtn/noseur";
import { ApiResponseData, BaseService, IMember, SanitizedResponse, EarningService, IMemberCurrencyBalance, ITransaction, TransactionType, TransactionSourceType } from "../../../services";
import { ChartManager } from "../../shared/ChartManager";

export interface ManageEarningsProps {
    profile: IMember;
}

function ManageEarnings(props: ManageEarningsProps) {

    const { profile } = props;
    const labels = i18nManager.Labels.earnings;
    const navigate = ReactRouterDom.useNavigate();
    const { height, width } = useScreenDimension();
    const earningService = EarningService.getInstance();
    const earningsChartDataState = React.useState<ChartData>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [recentTransactions, setRecentTransactions] = React.useState<ApiResponseData<ITransaction>>({});
    const [currencyBalance, setCurrencyBalance] = React.useState<ApiResponseData<IMemberCurrencyBalance>>({});

    React.useEffect(() => {
        fetchInformations();
    }, [searchParams]);

    return (<div className="earnings">
        <div className="header">
            <div className="l">
                <span className="t">{labels.earnings}</span>
            </div>
            <div className="queries">
                <FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={labels.date_range_input.title} rightContent={"fa fa-calendar"} style={{ width: 360 }}>
                    <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT} formatDate={(date) => Utils.formatToFineDate4(date)}
                        selectedDates={[(searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()), new Date(searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                        selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            Utils.updateSearchParamses({
                                "to": Utils.formatToApiDate(options.toDate!, false, false),
                                "from": Utils.formatToApiDate(options.fromDate!, false, false),
                            }, setSearchParams, fetchInformations);
                        }} />
                </FormControl>
            </div>
        </div>
        <div className="currency-cards">
            {!currencyBalance.content && (Elements.LOADING_STATE2)}
            {currencyBalance.content?.map((currencyEarning) => {
                const currencyName = i18nManager.getCurrencyName(currencyEarning.currency_code);
                const currencySign = i18nManager.getCurrencySign(currencyEarning.currency_code);
                const currencyCountryFlag = i18nManager.getCurrencyCountryFlag(currencyEarning.currency_code);

                return (<div key={currencyEarning.external_id} className="currency-card" onClick={() => navigate(`/earnings/${currencyEarning.currency_code}`)}>
                    <img alt={currencyEarning.currency_code} src={currencyCountryFlag} />
                    <div className="top">
                        <span className="n lined-limit">{currencyName} ({currencyEarning.currency_code})</span>
                        <span className={Classname.build("tr", { "p": currencyEarning.percentage_increase > 0, "n": currencyEarning.percentage_increase < 0, "b": currencyEarning.percentage_increase == 0 })}>
                            {Elements.TREND_ICON}
                            <span className="lined-limit">{currencyEarning.percentage_increase} %</span>
                        </span>
                    </div>
                    <span className="value">{currencySign} {StringHelper.toPresentableMoneyValue(currencyEarning.available_amount)}</span>
                </div>);
            })}
        </div>
        <span className="hint">{labels.click_card_for_more} <span>{labels.scroll_for_more}</span></span>
        {(height > 1000) && (<ChartManager type={ChartType.LINE} title={labels.earning_chart} chartDataState={earningsChartDataState} />)}
        <div className="rtrans-header" style={height < 1000 ? { marginTop: 20 } : undefined}>
            <span className="l">{labels.recent_transactions}</span>
            <ReactRouterDom.NavLink to={"/earnings/transactions"}>{labels.view_all}</ReactRouterDom.NavLink>
        </div>
        <Table scheme={Schemes.OMNEHOST} data={recentTransactions.content} totalRecords={recentTransactions.total_elements} dataRefreshKeys={["external_id", "created_at"]}
            style={{ flex: 1 }} rowsPerPage={200} loadingState={Elements.LOADING_STATE} emptyState={Elements.buildEmptyListView("fa fa-wallet", labels.transactions.no_transaction, { marginTop: 70 })}>
            <Column className="table-c" header={labels.transactions.table.headers.date} dataKey="created_at" template={(v) => Utils.formatToFineDate(v, true)} />
            <Column className="table-c" header={labels.transactions.table.headers.source} template={(v: ITransaction) => {
                let link = EarningService.buildTransactionSourceLink(v);
                const label = v.source_data?.name ?? v.source_data?.data?.name;
                return (<div style={{ display: "flex", flexDirection: "row", alignItems: "center", }}>
                    {v.source_data?.image_location && (<img alt={label} src={v.source_data?.image_location} style={{ width: 20, height: 20, marginRight: 10, borderRadius: "100%" }} />)}
                    {link ? (<a target="_blank" href={link}>{label}</a>) : (<span>{label}</span>)}
                </div>);
            }} />
            <Column className="table-c" doNotRender={width < 800} header={labels.transactions.table.headers.description} dataKey="description" />
            <Column className="table-c" doNotRender={width < 800} header={labels.transactions.table.headers.amount} template={(v: ITransaction) => {
                const isWithdrawal = v.source_type === TransactionSourceType.WITHDRAWAL;
                return (<span style={isWithdrawal ? { color: "#FF7171", } : undefined}>{i18nManager.getCurrencySign(v.currency_code)} {isWithdrawal ? "-" : ""}{StringHelper.toPresentableMoneyValue(v.amount)}</span>);
            }} />
            <Column className="table-c" doNotRender={width < 800} header={labels.transactions.table.headers.platform_charge} template={(v: ITransaction) => {
                const isWithdrawal = v.source_type === TransactionSourceType.WITHDRAWAL;
                return (<span style={isWithdrawal ? { color: "#FF7171", } : undefined}>{i18nManager.getCurrencySign(v.currency_code)} {isWithdrawal ? "-" : ""}{StringHelper.toPresentableMoneyValue(v.platform_amount)} ({v.extra_data?.platform_charge_percentage ?? 0} %)</span>);
            }} />
            <Column className="table-c" doNotRender={width < 800} header={labels.transactions.table.headers.tax} template={(v: ITransaction) => {
                const isWithdrawal = v.source_type === TransactionSourceType.WITHDRAWAL;
                return (<span style={isWithdrawal ? { color: "#FF7171", } : undefined}>{i18nManager.getCurrencySign(v.currency_code)} {isWithdrawal ? "-" : ""}{StringHelper.toPresentableMoneyValue(v.tax_amount)} ({v.extra_data?.tax_amount_percentage ?? 0} %)</span>);
            }} />
            <Column className="table-c" header={labels.transactions.table.headers.amount_received} template={(v: ITransaction) => {
                const isWithdrawal = v.source_type === TransactionSourceType.WITHDRAWAL;
                return (<span style={isWithdrawal ? { color: "#FF7171", } : undefined}>{i18nManager.getCurrencySign(v.currency_code)} {isWithdrawal ? "-" : ""}{StringHelper.toPresentableMoneyValue(v.payable_amount)}</span>);
            }} />
        </Table>
    </div>);

    function fetchInformations() {
        fetchEarnings();
        fetchAnalytics();
        fetchRecentTransactions();
    }

    function fetchEarnings() {
        setCurrencyBalance({});
        earningService.queryCurrencyBalance({
            sort: "available_amount,DESC",
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date())!,
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth())!,
        }).then(({ apiResponse }: SanitizedResponse<IMemberCurrencyBalance>) => {
            setCurrencyBalance(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function fetchAnalytics() {
        earningsChartDataState[1](undefined);
        earningService.queryCurrencyAnalytics({
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!,
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth(), false, false)!,
        }).then(({ apiResponse }: SanitizedResponse<any>) => {
            earningsChartDataState[1](apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function fetchRecentTransactions() {
        setRecentTransactions({});
        earningService.queryTransactions({
            size: 20,
        }).then(({ apiResponse }: SanitizedResponse<any>) => {
            if (searchParams.has("test")) {
                setRecentTransactions({ content: [] });
                return;
            }
            setRecentTransactions(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default ManageEarnings;
